import { Button, Modal, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faSave } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { I18NextNS } from 'src/models/Language.model';
import { EyesPosition } from 'src/models/Session.model';

import useActionAnalyzeModel from 'src/hooks/useActionAnalyzeModel';

import RetakeActionButtons from './RetakeActionButtons';

interface ProgressModalFooterProps {
  handleRetake: (position: EyesPosition) => void;
  hasCompletedExam: boolean;
  shouldShowDilationRecommendation: boolean;
  shouldShowReachMaxRecommendedAttempts: boolean;
  handleCloseAnalyzingProgressModal: () => void;
  handleResetSession: () => void;
}

const ProgressModalFooter = ({
  handleRetake,
  hasCompletedExam,
  shouldShowDilationRecommendation,
  shouldShowReachMaxRecommendedAttempts,
  handleCloseAnalyzingProgressModal,
  handleResetSession,
}: ProgressModalFooterProps) => {
  const { t } = useTranslation(I18NextNS.PAGES);

  const { session } = useRemoteBoundStore();

  const {
    handleCompleteExam,
    handleSaveAndDilate,
    handleEndSession,
    isClosingSession,
  } = useActionAnalyzeModel(
    handleCloseAnalyzingProgressModal,
    handleResetSession,
    session,
  );

  return (
    <Modal.Footer>
      {isClosingSession ? (
        session.graded && (
          <Button variant="primary" disabled>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {t('newExam.saving')}
          </Button>
        )
      ) : (
        <Stack direction="horizontal" gap={4}>
          {session.graded ? (
            <>
              {(shouldShowReachMaxRecommendedAttempts || hasCompletedExam) && (
                <Button
                  variant="primary"
                  className="shadow"
                  onClick={handleCompleteExam}
                >
                  {t('newExam.completeExam')}
                </Button>
              )}

              {shouldShowDilationRecommendation && !hasCompletedExam && (
                <Button variant="primary" onClick={handleSaveAndDilate}>
                  {t('newExam.saveAndDilate')}
                </Button>
              )}

              {session.graded && (
                <RetakeActionButtons
                  handleRetake={handleRetake}
                  handleCloseAnalyzingProgressModal={
                    handleCloseAnalyzingProgressModal
                  }
                />
              )}

              {!hasCompletedExam && !shouldShowReachMaxRecommendedAttempts && (
                <Button
                  variant="warning"
                  disabled={!session.graded}
                  onClick={handleEndSession}
                >
                  {t('newExam.endSession')}
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="warning"
              disabled={!session.graded}
              onClick={handleEndSession}
            >
              {t('newExam.endSession')}
            </Button>
          )}
        </Stack>
      )}
    </Modal.Footer>
  );
};

export default ProgressModalFooter;
